import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TestResult {
  template: string;
  status: 'success' | 'error' | 'loading';
  message: string;
  timestamp: string;
}

interface TestData {
  email: string;
  nickname: string;
  mentorshipTitle: string;
  mentorName: string;
  playerName: string;
  scheduledDate: string;
  scheduledTime: string;
  meetingLink: string;
  amountCents: number;
  bankName: string;
  accountNumber: string;
  transactionId: string;
}

const EmailTemplateTestPage = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [testData, setTestData] = useState<TestData>({
    email: "",
    nickname: "",
    mentorshipTitle: "",
    mentorName: "",
    playerName: "",
    scheduledDate: "",
    scheduledTime: "",
    meetingLink: "",
    amountCents: 5000,
    bankName: "",
    accountNumber: "",
    transactionId: ""
  });

  const templates = [
    { id: 'welcome', name: 'Boas-vindas Jogador', category: 'player' },
    { id: 'purchaseConfirmation', name: 'Confirmação de Compra', category: 'player' },
    { id: 'meetingLinkAdded', name: 'Link da Reunião Adicionado', category: 'player' },
    { id: 'sessionReminder', name: 'Lembrete de Sessão', category: 'player' },
    { id: 'paymentReleaseReminder', name: 'Lembrete de Liberação de Pagamento', category: 'player' },
    { id: 'mentorWelcome', name: 'Boas-vindas Mentor', category: 'mentor' },
    { id: 'mentorshipCreated', name: 'Mentoria Criada', category: 'mentor' },
    { id: 'mentorshipSold', name: 'Mentoria Vendida', category: 'mentor' },
    { id: 'sessionReminderMentor', name: 'Lembrete de Sessão (Mentor)', category: 'mentor' },
    { id: 'paymentReleased', name: 'Pagamento Liberado', category: 'mentor' },
    { id: 'withdrawalRequested', name: 'Saque Solicitado', category: 'mentor' },
    { id: 'withdrawalApproved', name: 'Saque Aprovado', category: 'mentor' }
  ];

  const playerTemplates = templates.filter(t => t.category === 'player');
  const mentorTemplates = templates.filter(t => t.category === 'mentor');

  const getTemplateData = (templateId: string) => {
    const baseData = {
      appUrl: window.location.origin
    };

    switch (templateId) {
      case 'welcome':
      case 'mentorWelcome':
        return { ...baseData, nickname: testData.nickname };
      
      case 'purchaseConfirmation':
      case 'meetingLinkAdded':
      case 'sessionReminder':
      case 'paymentReleaseReminder':
        return {
          ...baseData,
          mentorshipTitle: testData.mentorshipTitle,
          mentorName: testData.mentorName,
          scheduledDate: testData.scheduledDate,
          scheduledTime: testData.scheduledTime,
          meetingLink: testData.meetingLink
        };
      
      case 'mentorshipCreated':
        return {
          ...baseData,
          title: testData.mentorshipTitle,
          gameName: "Jogo de Teste",
          durationMinutes: 60,
          priceCents: testData.amountCents
        };
      
      case 'mentorshipSold':
      case 'sessionReminderMentor':
      case 'paymentReleased':
        return {
          ...baseData,
          mentorshipTitle: testData.mentorshipTitle,
          playerName: testData.playerName,
          scheduledDate: testData.scheduledDate,
          scheduledTime: testData.scheduledTime,
          amountCents: testData.amountCents
        };
      
      case 'withdrawalRequested':
      case 'withdrawalApproved':
        return {
          ...baseData,
          amountCents: testData.amountCents,
          bankName: testData.bankName,
          accountNumber: testData.accountNumber,
          requestDate: testData.scheduledDate,
          processedDate: testData.scheduledDate,
          transactionId: testData.transactionId
        };
      
      default:
        return baseData;
    }
  };

  const testEmail = async (templateId: string) => {
    if (!testData.email) {
      alert('Por favor, preencha o email de destino');
      return;
    }

    setIsLoading(prev => ({ ...prev, [templateId]: true }));
    
    const newResult: TestResult = {
      template: templateId,
      status: 'loading',
      message: 'Enviando...',
      timestamp: new Date().toLocaleTimeString()
    };
    
    setTestResults(prev => [newResult, ...prev.filter(r => r.template !== templateId)]);

    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: testData.email,
          template: templateId,
          data: getTemplateData(templateId)
        }
      });

      if (error) throw error;

      setTestResults(prev => prev.map(r => 
        r.template === templateId 
          ? { ...r, status: 'success', message: 'Email enviado com sucesso!' }
          : r
      ));
    } catch (error: any) {
      setTestResults(prev => prev.map(r => 
        r.template === templateId 
          ? { ...r, status: 'error', message: error.message || 'Erro desconhecido' }
          : r
      ));
    } finally {
      setIsLoading(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const testAllEmails = async () => {
    if (!testData.email) {
      alert('Por favor, preencha o email de destino');
      return;
    }

    for (const template of templates) {
      await testEmail(template.id);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1117] via-[#0f141c] to-[#0c1117] p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-white">
            Sistema de Teste de Templates de Email
          </h1>
          <p className="text-xl text-gray-300">
            Teste todos os templates de email da Arena Pro Player
          </p>
        </div>

        {/* Formulário de dados de teste */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Dados para Teste</CardTitle>
            <CardDescription>
              Preencha os dados que serão usados nos templates de email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-white">Email de Destino *</Label>
                <Input
                  id="email"
                  type="email"
                  value={testData.email}
                  onChange={(e) => setTestData({...testData, email: e.target.value})}
                  placeholder="seu@email.com"
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="nickname" className="text-white">Nome/Nickname</Label>
                <Input
                  id="nickname"
                  value={testData.nickname}
                  onChange={(e) => setTestData({...testData, nickname: e.target.value})}
                  placeholder="João Silva"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="mentorshipTitle" className="text-white">Título da Mentoria</Label>
                <Input
                  id="mentorshipTitle"
                  value={testData.mentorshipTitle}
                  onChange={(e) => setTestData({...testData, mentorshipTitle: e.target.value})}
                  placeholder="Mentoria de League of Legends"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="mentorName" className="text-white">Nome do Mentor</Label>
                <Input
                  id="mentorName"
                  value={testData.mentorName}
                  onChange={(e) => setTestData({...testData, mentorName: e.target.value})}
                  placeholder="Coach Master"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="playerName" className="text-white">Nome do Jogador</Label>
                <Input
                  id="playerName"
                  value={testData.playerName}
                  onChange={(e) => setTestData({...testData, playerName: e.target.value})}
                  placeholder="João Silva"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="scheduledDate" className="text-white">Data Agendada</Label>
                <Input
                  id="scheduledDate"
                  value={testData.scheduledDate}
                  onChange={(e) => setTestData({...testData, scheduledDate: e.target.value})}
                  placeholder="25 de Janeiro de 2024"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="scheduledTime" className="text-white">Horário</Label>
                <Input
                  id="scheduledTime"
                  value={testData.scheduledTime}
                  onChange={(e) => setTestData({...testData, scheduledTime: e.target.value})}
                  placeholder="15:00"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="meetingLink" className="text-white">Link da Reunião</Label>
                <Input
                  id="meetingLink"
                  value={testData.meetingLink}
                  onChange={(e) => setTestData({...testData, meetingLink: e.target.value})}
                  placeholder="https://meet.google.com/abc-defg-hij"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="amountCents" className="text-white">Valor (centavos)</Label>
                <Input
                  id="amountCents"
                  type="number"
                  value={testData.amountCents}
                  onChange={(e) => setTestData({...testData, amountCents: parseInt(e.target.value)})}
                  placeholder="5000"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="bankName" className="text-white">Nome do Banco</Label>
                <Input
                  id="bankName"
                  value={testData.bankName}
                  onChange={(e) => setTestData({...testData, bankName: e.target.value})}
                  placeholder="Banco do Brasil"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="accountNumber" className="text-white">Número da Conta</Label>
                <Input
                  id="accountNumber"
                  value={testData.accountNumber}
                  onChange={(e) => setTestData({...testData, accountNumber: e.target.value})}
                  placeholder="12345-6"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="transactionId" className="text-white">ID da Transação</Label>
                <Input
                  id="transactionId"
                  value={testData.transactionId}
                  onChange={(e) => setTestData({...testData, transactionId: e.target.value})}
                  placeholder="TXN-123456789"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            
            <div className="flex justify-center pt-4">
              <Button 
                onClick={testAllEmails}
                className="bg-[#00B6F0] hover:bg-[#00B6F0]/80 text-white px-8 py-2"
                disabled={!testData.email}
              >
                <Mail className="w-4 h-4 mr-2" />
                Testar Todos os Templates
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Templates de Jogador */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Templates para Jogadores ({playerTemplates.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playerTemplates.map((template) => (
                <Card key={template.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-white mb-2">{template.name}</h3>
                    <Button
                      onClick={() => testEmail(template.id)}
                      disabled={isLoading[template.id] || !testData.email}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isLoading[template.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Testar'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Templates de Mentor */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Templates para Mentores ({mentorTemplates.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mentorTemplates.map((template) => (
                <Card key={template.id} className="bg-gray-800/50 border-gray-700">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-white mb-2">{template.name}</h3>
                    <Button
                      onClick={() => testEmail(template.id)}
                      disabled={isLoading[template.id] || !testData.email}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isLoading[template.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Testar'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Resultados dos Testes */}
        {testResults.length > 0 && (
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Resultados dos Testes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {testResults.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {result.status === 'success' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                      {result.status === 'error' && <XCircle className="w-5 h-5 text-red-500" />}
                      {result.status === 'loading' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
                      <span className="text-white font-medium">{result.template}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-300 text-sm">{result.message}</span>
                      <Badge variant={result.status === 'success' ? 'default' : result.status === 'error' ? 'destructive' : 'secondary'}>
                        {result.timestamp}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informações do Sistema */}
        <div className="mt-12 p-6 bg-white rounded-lg border">
          <h3 className="text-lg font-semibold mb-3">📋 Informações do Sistema</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><strong>Edge Function:</strong> send-email</p>
              <p><strong>Total de templates:</strong> {templates.length}</p>
            </div>
            <div>
              <p><strong>Jogador:</strong> {playerTemplates.length} templates</p>
              <p><strong>Mentor:</strong> {mentorTemplates.length} templates</p>
            </div>
          </div>
          <Separator className="my-4" />
          <p className="text-xs text-gray-500">
            💡 <strong>Dica:</strong> Preencha todos os campos relevantes para testar os templates corretamente.
            Verifique sua caixa de entrada após os testes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateTestPage; 